import { useEffect, useState, useCallback, useRef } from 'react';
import LightboxModal from './LightboxModal';
import ChatMessageItem from './ChatMessageItem';
import useIntersectionObserver from 'src/hooks/useIntersectionObserver';
import { debounce } from 'lodash';
import { useDispatch } from 'react-redux';
import { getConversationMessages } from 'src/redux/thunks/conversation';
import ChatMessageListScrollbar from './ChatMessageListScrollbar';
import { LinearProgress } from '@mui/material';
import { useSnackbar } from 'notistack';
import ChatAudioProvider from '../context/ChatAudioContext';
import ChatSystemMessage from './ChatSystemMessage';

export default function ChatMessageList({
  conversation,
  handleMarkConversationAsRead,
  handleResendMessage,
}) {
  const [openLightbox, setOpenLightbox] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);
  const [topObserver, setTopObserver] = useState(null);
  const [bottomObserver, setBottomObserver] = useState(null);
  const topObserverEntry = useIntersectionObserver(topObserver);
  const bottomObserverEntry = useIntersectionObserver(bottomObserver);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [totalMessages, setTotalMessages] = useState(0);
  const { enqueueSnackbar } = useSnackbar();
  const messagesContainerRef = useRef(null);

  const fetchMoreMessages = useCallback(
    debounce(async () => {
      setLoading(true);
      try {
        const result = await dispatch(
          getConversationMessages({
            conversationId: conversation.id,
            skip: conversation.messages.length,
          })
        ).unwrap();

        messagesContainerRef.current?.firstElementChild.scrollIntoView();

        setTotalMessages(result.totalMessages);
      } catch (error) {
        enqueueSnackbar('error loading messages', {
          variant: 'error',
        });
      } finally {
        setLoading(false);
      }
    }, 200),
    [conversation]
  );

  useEffect(() => {
    if (totalMessages === conversation.messages.length) return;
    if (topObserverEntry.isIntersecting && conversation.id) {
      fetchMoreMessages();
    }
  }, [topObserverEntry]);

  useEffect(() => {
    if (bottomObserverEntry.isIntersecting && conversation.unread) {
      handleMarkConversationAsRead();
    }
  }, [bottomObserverEntry, conversation]);

  const imagesLightbox = conversation.messages
    .filter((messages) => messages.contentType === 'image')
    .map((messages) => messages.body);

  const handleOpenLightbox = (url) => {
    const selectedImage = imagesLightbox.findIndex((index) => index === url);
    setOpenLightbox(true);
    setSelectedImage(selectedImage);
  };

  return (
    <>
      <ChatMessageListScrollbar
        conversation={conversation}
        showScrollButton={!bottomObserverEntry.isIntersecting}
      >
        <LinearProgress
          sx={{
            visibility: loading ? 'visible' : 'hidden',
          }}
        />
        <div ref={(ref) => setTopObserver(ref)} />
        <div ref={messagesContainerRef}>
          <ChatAudioProvider>
            {conversation.messages.map((message) =>
              message.type === 'system' ? (
                <ChatSystemMessage
                  key={message.id}
                  message={message}
                  participants={conversation.participants}
                />
              ) : (
                <ChatMessageItem
                  key={message.id}
                  message={message}
                  conversation={conversation}
                  onOpenLightbox={handleOpenLightbox}
                  onResendMessage={handleResendMessage}
                />
              )
            )}
          </ChatAudioProvider>
        </div>
        <div ref={(ref) => setBottomObserver(ref)} />
      </ChatMessageListScrollbar>
      <LightboxModal
        images={imagesLightbox}
        mainSrc={imagesLightbox[selectedImage]}
        photoIndex={selectedImage}
        setPhotoIndex={setSelectedImage}
        isOpen={openLightbox}
        onCloseRequest={() => setOpenLightbox(false)}
      />
    </>
  );
}
